import { useState } from 'react';
import { Clock, Copy, Check, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

interface TimestampFormat {
  style: string;
  code: string;
  description: string;
  example: string;
}

const DiscordTimestamp = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState(new Date().toTimeString().slice(0, 5));
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const getUnixTimestamp = () => {
    const dateTime = new Date(`${date}T${time}`);
    return Math.floor(dateTime.getTime() / 1000);
  };

  const timestamp = getUnixTimestamp();

  const formats: TimestampFormat[] = [
    {
      style: 't',
      code: `<t:${timestamp}:t>`,
      description: 'Short Time',
      example: new Date(timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    },
    {
      style: 'T',
      code: `<t:${timestamp}:T>`,
      description: 'Long Time',
      example: new Date(timestamp * 1000).toLocaleTimeString(),
    },
    {
      style: 'd',
      code: `<t:${timestamp}:d>`,
      description: 'Short Date',
      example: new Date(timestamp * 1000).toLocaleDateString(),
    },
    {
      style: 'D',
      code: `<t:${timestamp}:D>`,
      description: 'Long Date',
      example: new Date(timestamp * 1000).toLocaleDateString([], { dateStyle: 'long' }),
    },
    {
      style: 'f',
      code: `<t:${timestamp}:f>`,
      description: 'Short Date/Time',
      example: new Date(timestamp * 1000).toLocaleString([], { dateStyle: 'long', timeStyle: 'short' }),
    },
    {
      style: 'F',
      code: `<t:${timestamp}:F>`,
      description: 'Long Date/Time',
      example: new Date(timestamp * 1000).toLocaleString([], { dateStyle: 'full', timeStyle: 'short' }),
    },
    {
      style: 'R',
      code: `<t:${timestamp}:R>`,
      description: 'Relative Time',
      example: getRelativeTime(timestamp),
    },
  ];

  function getRelativeTime(unixTime: number): string {
    const now = Math.floor(Date.now() / 1000);
    const diff = unixTime - now;
    const absDiff = Math.abs(diff);
    
    if (absDiff < 60) return diff > 0 ? 'in a few seconds' : 'a few seconds ago';
    if (absDiff < 3600) {
      const mins = Math.floor(absDiff / 60);
      return diff > 0 ? `in ${mins} minute${mins > 1 ? 's' : ''}` : `${mins} minute${mins > 1 ? 's' : ''} ago`;
    }
    if (absDiff < 86400) {
      const hours = Math.floor(absDiff / 3600);
      return diff > 0 ? `in ${hours} hour${hours > 1 ? 's' : ''}` : `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    const days = Math.floor(absDiff / 86400);
    return diff > 0 ? `in ${days} day${days > 1 ? 's' : ''}` : `${days} day${days > 1 ? 's' : ''} ago`;
  }

  const copyCode = (code: string, index: number) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const setToNow = () => {
    const now = new Date();
    setDate(now.toISOString().split('T')[0]);
    setTime(now.toTimeString().slice(0, 5));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Clock className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Discord Timestamp Generator</h1>
          <p className="text-muted-foreground">
            Create dynamic timestamps for Discord messages
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Date/Time Picker */}
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Select Date & Time
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Time</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
            <Button variant="outline" onClick={setToNow} className="w-full">
              Set to Now
            </Button>
            <div className="p-3 rounded-lg bg-muted/30 text-center">
              <p className="text-xs text-muted-foreground">Unix Timestamp</p>
              <p className="text-lg font-mono font-bold">{timestamp}</p>
            </div>
          </CardContent>
        </Card>

        {/* Formats */}
        <Card className="glass border-border/50 lg:col-span-2">
          <CardHeader>
            <CardTitle>Timestamp Formats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {formats.map((format, index) => (
                <div
                  key={format.style}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors group"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <code className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary font-mono">
                        {format.style}
                      </code>
                      <span className="text-sm font-medium">{format.description}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{format.example}</p>
                    <code className="text-xs font-mono text-muted-foreground mt-1 block">
                      {format.code}
                    </code>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyCode(format.code, index)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    {copiedIndex === index ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DiscordTimestamp;